import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";

import { EnquiryForm } from "@/components/enquiry-form";
import { PageHero } from "@/components/page-parts";
import { FIRM, FIRM_ADDRESS, MICHAEL_PHONE, PENINSULA_ADDRESS } from "@/lib/firm";
import { PROBATE_FEES } from "@/lib/probate-locations";

const URL = "https://coastsidelaw.com.au/probate-enquiry";
const TITLE = "Probate Enquiry for Executors — Coastside Law Queensland";
const DESCRIPTION = `Executors: contact Coastside Law directly about probate, a court requisition or an estate in Queensland. Fixed-fee grants from ${PROBATE_FEES.grant}. Call ${FIRM.phone}.`;

export const Route = createFileRoute("/probate-enquiry")({
  head: () => ({
    meta: [
      { title: fitTitle(TITLE) },
      { name: "description", content: fitDescription(DESCRIPTION) },
      { property: "og:title", content: fitTitle(TITLE) },
      { property: "og:description", content: fitDescription(DESCRIPTION) },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: ProbateEnquiry,
});

function ProbateEnquiry() {
  return (
    <>
      <PageHero
        eyebrow="Executors"
        title="Probate enquiry"
        lead="Been named as executor, or had a probate application sent back by the court? Tell us where things stand and a solicitor will come back to you, usually the same business day."
      />

      <section className="mx-auto grid max-w-6xl gap-10 px-5 py-16 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
        <div className="min-w-0">
          <h2 className="font-serif text-2xl text-foreground">Helpful to include</h2>
          <ul className="mt-4 space-y-2.5">
            {[
              "The name of the person who died and the date of death",
              "Whether you have the original will, and where it is kept",
              "A rough idea of the assets: house, bank accounts, shares",
              "Any letter or requisition you have received from the Supreme Court",
              "Any family disagreement or likely claim on the estate",
            ].map((i) => (
              <li key={i} className="flex gap-3 text-sm leading-relaxed text-muted-foreground">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                <span>{i}</span>
              </li>
            ))}
          </ul>

          <div className="mt-8 rounded-sm border border-border bg-card p-6">
            <p className="text-sm font-semibold text-foreground">Prefer to talk?</p>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Call <a href={FIRM.phoneHref} className="font-semibold text-primary underline">{FIRM.phone}</a>{" "}
              or Michael Klein on {MICHAEL_PHONE}. Offices at {FIRM_ADDRESS} and {PENINSULA_ADDRESS}.
            </p>
            <p className="mt-3 text-sm text-muted-foreground">
              Straightforward grants are a fixed fee from {PROBATE_FEES.grant} including GST.
            </p>
          </div>

          <p className="mt-8 text-sm text-muted-foreground">
            Getting started?{" "}
            <Link to="/probate-checklist-queensland" className="font-semibold text-primary underline">
              Read the executor's probate checklist
            </Link>
            .
          </p>
        </div>

        <div className="min-w-0 rounded-sm border border-border bg-card p-6 sm:p-8">
          <EnquiryForm heading="Send your probate enquiry" defaultMatterType="wills-estates" />
        </div>
      </section>
    </>
  );
}
